/**
 * HealthTab — iter63 Phase 3 verification dashboard.
 *
 * Four cards on one admin tab:
 *   A) Feed Health   — Google Shopping XML / Meta Catalog / Ready-Made
 *      TSV / Local Inventory. Runs background check, shows per-feed
 *      item count / invalid count / broken image URLs / issue codes.
 *   B) Pixel Health  — Meta Pixel + CAPI + GA4 config + last 24h/7d/30d
 *      CAPI event counts + last error. "Send Test Event" button for
 *      PageView / ViewContent / AddToCart / Purchase.
 *   C) Data Audit    — /db.products audit (broken URLs, missing back
 *      images, description/title length, duplicate slug/sku).
 *   D) Cron Timeline — last N feed/audit runs as a green/red strip.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, CheckCircle2, AlertCircle, RefreshCw, Rss, Zap,
  Database, ExternalLink, Send, Download, Wand2, Tag, X, RotateCcw, FileText,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminHealthFeedsRun, adminHealthFeedsStatus,
  adminHealthPixels, adminHealthPixelTest,
  adminHealthAuditRun, adminHealthAuditStatus,
  adminHealthTimeline,
  adminCategoriesNormalize,
  adminHealthAuditDismiss, adminHealthAuditUndismiss, adminHealthAuditDismissals,
  adminSanmarScrapeSpecs, adminSanmarScrapeSpecsStatus,
} from "../../lib/api";
// iter76c — moved from DashboardTab so all verification cards live in one place
import { WebhookHealthCard, WebhookDeliveriesCard, AltTextAuditCard } from "./DashboardTab";

// iter65 — inline client for the auto-fix endpoint (kept local to
// avoid bloating lib/api.js with another one-off admin call).
const auditAutoFix = async (pw, dry_run) => {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const r = await fetch(`${backend}/api/admin/health/audit/auto-fix`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Admin-Password": pw },
    body: JSON.stringify({ dry_run }),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

// iter67 — inline client for the new "scrape SanMar → refresh audit
// products" endpoint. Fetches the SanMar Canada catalog page for every
// product flagged in the latest audit, extracts real form-front and
// form-back image URLs, and writes them into color_images /
// back_color_images. Fixes cloned-back placeholders + broken URL
// formats introduced by legacy imports.
const auditScrapeBacks = async (pw) => {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const r = await fetch(`${backend}/api/admin/health/audit/scrape-backs`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Admin-Password": pw },
    body: JSON.stringify({}),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
};
const fmtMs = (n) => (n == null ? "—" : `${n}ms`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function StatusPill({ ok, label, muted }) {
  const cls = ok
    ? "border-emerald-800 bg-emerald-950/40 text-emerald-300"
    : muted
      ? "border-zinc-800 bg-zinc-950/40 text-zinc-400"
      : "border-red-900 bg-red-950/40 text-red-300";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[10px] tracking-widest ${cls}`}>
      {ok ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />} {label}
    </span>
  );
}

// ============================================================
// A · FEED HEALTH CARD
// ============================================================
function FeedHealthCard({ pw }) {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [pollTimer, setPollTimer] = useState(null);

  const load = useCallback(async () => {
    try { setStatus(await adminHealthFeedsStatus(pw)); }
    catch (e) { /* ignore */ }
  }, [pw]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => pollTimer && clearInterval(pollTimer), [pollTimer]);

  const run = async () => {
    if (running) return;
    setRunning(true);
    try {
      const k = await adminHealthFeedsRun(pw);
      if (!k?.ok) throw new Error(k?.detail || "failed to queue");
      toast.info("Feed health check queued.");
      const t = setInterval(async () => {
        const s = await adminHealthFeedsStatus(pw);
        setStatus(s);
        if (s?.phase === "done" || s?.phase === "failed") {
          clearInterval(t); setPollTimer(null); setRunning(false);
          toast[s.ok ? "success" : "warning"](
            s.ok
              ? `All ${(s.feeds || []).length} feeds healthy — ${s.total_items} items`
              : `Feed issues: ${s.total_invalid} invalid across ${(s.feeds || []).length} feeds`
          );
        }
      }, 3000);
      setPollTimer(t);
      setTimeout(() => { clearInterval(t); setPollTimer(null); setRunning(false); }, 180000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Feed health failed");
      setRunning(false);
    }
  };

  return (
    <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3" data-testid="health-feeds-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
            <Rss size={16} className="text-emerald-400" /> Feed Health
          </h3>
          <p className="font-mono text-[11px] text-zinc-500 mt-1">
            Google Shopping XML · Meta Catalog XML · Ready-Made TSV · Local Inventory TSV. Validated against Merchant Center rules.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={running}
          data-testid="health-feeds-run"
          className="bg-emerald-800 border border-emerald-700 text-white px-4 py-2 font-mono text-xs tracking-widest hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw size={13} className={running ? "animate-spin" : ""} />
          {running ? "RUNNING…" : "RUN CHECK NOW"}
        </button>
      </div>

      {status?.feeds ? (
        <div className="space-y-2">
          {status.feeds.map((f) => (
            <div key={f.key} className={`border p-3 ${f.ok && (f.invalid_items || 0) === 0 && (f.broken_images?.length || 0) === 0
              ? "border-emerald-900 bg-emerald-950/10"
              : f.ok
                ? "border-amber-900 bg-amber-950/10"
                : "border-red-900 bg-red-950/10"}`}
              data-testid={`feed-row-${f.key}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-zinc-100">{f.label}</span>
                  <StatusPill ok={f.ok} label={`HTTP ${f.status ?? "ERR"}`} />
                  <span className="font-mono text-[10px] text-zinc-500">{fmtMs(f.elapsed_ms)} · {fmtBytes(f.size_bytes)}</span>
                </div>
                <a href={f.url} target="_blank" rel="noreferrer" className="font-mono text-[10px] tracking-widest text-blue-300 hover:text-blue-200 flex items-center gap-1">
                  OPEN <ExternalLink size={10} />
                </a>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                <div className="font-mono text-[10px]"><span className="text-zinc-500">ITEMS </span><span className="text-white">{f.item_count ?? 0}</span></div>
                <div className="font-mono text-[10px]"><span className="text-zinc-500">INVALID </span><span className={f.invalid_items ? "text-amber-300" : "text-emerald-300"}>{f.invalid_items ?? 0}</span></div>
                <div className="font-mono text-[10px]"><span className="text-zinc-500">IMGS CHK </span><span className="text-white">{f.images_checked ?? 0}</span></div>
                <div className="font-mono text-[10px]"><span className="text-zinc-500">BROKEN IMG </span><span className={f.broken_images?.length ? "text-red-300" : "text-emerald-300"}>{f.broken_images?.length ?? 0}</span></div>
              </div>
              {f.issues?.length > 0 && (
                <div className="mt-2 font-mono text-[10px] text-amber-300">
                  <span className="tracking-widest">ISSUES: </span>
                  {f.issues.slice(0, 6).map((i, ix) => (
                    <span key={ix} className="border border-amber-900 px-1.5 py-0.5 mr-1">{i.code}×{i.count}</span>
                  ))}
                </div>
              )}
              {f.sample_bad?.length > 0 && (
                <details className="mt-2">
                  <summary className="font-mono text-[10px] text-zinc-400 cursor-pointer">Show {f.sample_bad.length} sample invalid items</summary>
                  <div className="mt-1 space-y-1 pl-3 border-l border-[#222]">
                    {f.sample_bad.map((s, ix) => (
                      <div key={ix} className="font-mono text-[10px] text-zinc-400">
                        <span className="text-amber-300">{s.id}</span> · {s.title} · <span className="text-red-300">{s.problems.join(", ")}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {f.error && <div className="font-mono text-[10px] text-red-300 mt-1">Error: {f.error}</div>}
            </div>
          ))}
          {status.finished_at && (
            <div className="font-mono text-[9px] text-zinc-600 pt-2">
              Last check: {fmtTime(status.finished_at)} · run_id: {status.run_id}
            </div>
          )}
        </div>
      ) : (
        <div className="font-mono text-xs text-zinc-500">No feed check has run yet — click RUN CHECK NOW.</div>
      )}
    </div>
  );
}

// ============================================================
// B · PIXEL HEALTH CARD
// ============================================================
function PixelHealthCard({ pw }) {
  const [health, setHealth] = useState(null);
  const [sending, setSending] = useState("");

  const load = useCallback(async () => {
    try { setHealth(await adminHealthPixels(pw)); }
    catch (_e) { /* ignore */ }
  }, [pw]);

  useEffect(() => { load(); }, [load]);

  const fireTest = async (event_name) => {
    setSending(event_name);
    try {
      const value = event_name === "Purchase" ? 42.00 : (event_name === "AddToCart" ? 24.99 : 0);
      const r = await adminHealthPixelTest(pw, { event_name, value });
      if (r?.ok) {
        toast.success(`✓ ${event_name} sent — events_received=${r.response?.events_received ?? "?"} fbtrace=${r.response?.fbtrace_id?.slice(0, 12) || "?"}`);
      } else if (r?.skipped) {
        toast.warning("CAPI not configured — set Pixel ID + CAPI token in the Meta Pixel tab first.");
      } else {
        toast.error(`✗ Meta rejected: ${JSON.stringify(r?.error || r).slice(0, 160)}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Test event failed");
    } finally { setSending(""); }
  };

  const mp = health?.meta_pixel || {};
  const capi = health?.meta_capi || {};
  const ga = health?.google_analytics || {};

  return (
    <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3" data-testid="health-pixels-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
            <Zap size={16} className="text-blue-400" /> Pixel &amp; Analytics
          </h3>
          <p className="font-mono text-[11px] text-zinc-500 mt-1">
            Verify Meta Pixel, CAPI, and GA4 are firing. Send synthetic events to confirm connectivity in Events Manager.
          </p>
        </div>
        <button type="button" onClick={load} data-testid="health-pixels-refresh"
          className="border border-[#333] text-zinc-300 hover:border-zinc-500 px-3 py-1.5 font-mono text-[11px] tracking-widest flex items-center gap-1">
          <RefreshCw size={11} /> REFRESH
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-2">
        <div className="border border-[#222] bg-black p-3 space-y-1">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">META PIXEL</div>
          <StatusPill ok={mp.configured} label={mp.configured ? "CONFIGURED" : "NOT SET"} muted={!mp.configured} />
          <div className="font-mono text-[11px] text-zinc-300">ID: {mp.pixel_id || "—"}</div>
        </div>
        <div className="border border-[#222] bg-black p-3 space-y-1">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">META CAPI (SERVER)</div>
          <StatusPill ok={capi.configured} label={capi.configured ? "CONFIGURED" : "NOT SET"} muted={!capi.configured} />
          <div className="font-mono text-[11px] text-zinc-300">
            24h: <span className="text-emerald-300">{capi.sent_ok_24h}✓</span>
            {capi.sent_err_24h > 0 && <span className="text-red-300"> {capi.sent_err_24h}✗</span>}
            <span className="text-zinc-500"> · 7d: {capi.sent_ok_7d}</span>
            <span className="text-zinc-500"> · 30d: {capi.sent_ok_30d}</span>
          </div>
        </div>
        <div className="border border-[#222] bg-black p-3 space-y-1">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">GOOGLE ANALYTICS 4</div>
          <StatusPill ok={ga.configured} label={ga.configured ? "CONFIGURED" : "NOT SET"} muted={!ga.configured} />
          <div className="font-mono text-[11px] text-zinc-300">ID: {ga.ga4_measurement_id || "—"}</div>
        </div>
      </div>

      {/* Event breakdown last 7d */}
      {capi.events_last_7d?.length > 0 && (
        <div className="border border-[#222] bg-black p-3">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">CAPI EVENTS · LAST 7 DAYS</div>
          <div className="flex flex-wrap gap-2">
            {capi.events_last_7d.map((e) => (
              <span key={e.event_name} className="border border-[#333] px-2 py-1 font-mono text-[10px] text-zinc-300">
                {e.event_name} · <span className="text-emerald-300">{e.ok}</span>/<span className="text-white">{e.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Test events */}
      <div className="border border-blue-900 bg-blue-950/10 p-3">
        <div className="font-mono text-[10px] tracking-widest text-blue-200 mb-2">SEND TEST EVENT (via CAPI)</div>
        <div className="flex flex-wrap gap-2">
          {["PageView", "ViewContent", "AddToCart", "InitiateCheckout", "Purchase"].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => fireTest(n)}
              disabled={!capi.configured || !!sending}
              data-testid={`pixel-test-${n}`}
              className="border border-blue-800 bg-blue-900/30 text-blue-100 hover:bg-blue-800/50 px-3 py-1.5 font-mono text-[11px] tracking-widest disabled:opacity-40 flex items-center gap-1"
            >
              <Send size={10} className={sending === n ? "animate-pulse" : ""} />
              {sending === n ? "SENDING…" : n.toUpperCase()}
            </button>
          ))}
        </div>
        {!capi.configured && (
          <div className="font-mono text-[10px] text-amber-300 mt-2">
            CAPI not configured yet — go to <span className="text-white">Admin → Meta Pixel</span> and set both the Pixel ID and the CAPI token to enable test events.
          </div>
        )}
      </div>

      {capi.last_error && (
        <div className="border border-red-900 bg-red-950/10 p-3">
          <div className="font-mono text-[10px] tracking-widest text-red-200 mb-1">MOST RECENT CAPI ERROR</div>
          <div className="font-mono text-[10px] text-zinc-300 break-all">
            {capi.last_error.event_name} · {fmtTime(capi.last_error.created_at)} · status {capi.last_error.status ?? "?"} · {String(capi.last_error.error || "").slice(0, 200)}
          </div>
        </div>
      )}
      {capi.last_ok && (
        <div className="font-mono text-[10px] text-zinc-500">
          Last successful send: {capi.last_ok.event_name} · {fmtTime(capi.last_ok.created_at)} · events_received={capi.last_ok.events_received ?? "?"} · fbtrace={String(capi.last_ok.fbtrace_id || "").slice(0, 12)}
        </div>
      )}
    </div>
  );
}

// ============================================================
// C · DATA AUDIT CARD
// ============================================================
function DataAuditCard({ pw }) {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [pollTimer, setPollTimer] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [autofixing, setAutofixing] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [dismissals, setDismissals] = useState([]);
  const [dismissalsExpanded, setDismissalsExpanded] = useState(false);
  const [dismissingKey, setDismissingKey] = useState(null); // `${pid}::${code}`
  // iter76 — spec-sheet scraper trigger from the audit card
  const [specStatus, setSpecStatus] = useState(null);
  const [specBusy, setSpecBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus(await adminHealthAuditStatus(pw)); }
    catch (_e) { /* ignore */ }
  }, [pw]);
  const loadDismissals = useCallback(async () => {
    try {
      const r = await adminHealthAuditDismissals(pw);
      setDismissals(r?.items || []);
    } catch (_e) { /* ignore */ }
  }, [pw]);
  useEffect(() => { load(); loadDismissals(); }, [load, loadDismissals]);
  useEffect(() => () => pollTimer && clearInterval(pollTimer), [pollTimer]);

  // iter76 — spec scraper state polling
  const loadSpecStatus = useCallback(async () => {
    try { setSpecStatus(await adminSanmarScrapeSpecsStatus(pw)); } catch (_e) { /* ignore */ }
  }, [pw]);
  useEffect(() => { loadSpecStatus(); }, [loadSpecStatus]);
  const specRunning = specBusy || specStatus?.last_run?.phase === "running";
  useEffect(() => {
    if (!specRunning) return;
    const id = setInterval(loadSpecStatus, 3000);
    return () => clearInterval(id);
  }, [specRunning, loadSpecStatus]);
  useEffect(() => {
    if (!specBusy) return;
    const phase = specStatus?.last_run?.phase;
    if (phase === "done" || phase === "error") {
      setSpecBusy(false);
      const lr = specStatus?.last_run || {};
      if (phase === "error") toast.error(`Spec scrape errored: ${lr.error}`);
      else toast.success(`Spec scrape done · ${lr.success} succeeded · ${lr.failed} had no PDF`);
      // Re-run the audit to reflect newly-populated specs
      load();
    }
  }, [specBusy, specStatus?.last_run, load]);

  const scrapeSpecs = async () => {
    setSpecBusy(true);
    try {
      const r = await adminSanmarScrapeSpecs(pw, {});
      if (r.queued) toast.info(`Queued spec scrape ${r.run_id}. Live progress at bottom.`, { duration: 4000 });
      await loadSpecStatus();
    } catch (e) {
      setSpecBusy(false);
      toast.error(e?.response?.data?.detail || e?.message || "Failed to start scrape");
    }
  };

  const dismissCode = async (product, code) => {
    const key = `${product.id}::${code}`;
    const reason = window.prompt(
      `Mark this flag as OK — it will be hidden from future audits.\n\n` +
      `Product:  ${product.name}\n` +
      `Code:     ${code}\n\n` +
      `Reason (optional, e.g. "bag has no back photo"):`,
      ""
    );
    if (reason === null) return; // cancelled
    setDismissingKey(key);
    try {
      await adminHealthAuditDismiss(pw, { product_id: product.id, code, reason });
      // Optimistic: strip this code from the current status.problems
      setStatus((s) => {
        if (!s?.problems) return s;
        const problems = s.problems
          .map((row) => row.id === product.id
            ? { ...row, codes: row.codes.filter((c) => c !== code),
                dismissed_codes: [...(row.dismissed_codes || []), code] }
            : row)
          .filter((row) => row.codes.length > 0);
        return { ...s, problems, totals: { ...(s.totals || {}), with_issues: problems.length } };
      });
      loadDismissals();
      toast.success(`Dismissed "${code}" for ${product.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Dismiss failed");
    } finally {
      setDismissingKey(null);
    }
  };

  const undismiss = async (d) => {
    const key = `${d.product_id}::${d.code_prefix}`;
    setDismissingKey(key);
    try {
      await adminHealthAuditUndismiss(pw, { product_id: d.product_id, code: d.code_prefix });
      toast.success(`Restored "${d.code_prefix}" — re-run audit to re-check`);
      loadDismissals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Undo failed");
    } finally {
      setDismissingKey(null);
    }
  };

  const scrapeFromSanmar = async () => {
    if (scraping) return;
    const proceed = window.confirm(
      "This will fetch sanmarcanada.com for every product flagged in the latest audit " +
      "(no_back_images + broken_image_urls) and pull REAL front/back photos where available. " +
      "Takes ~2-5s per product. Continue?"
    );
    if (!proceed) return;
    setScraping(true);
    try {
      toast.info("Scraping SanMar Canada — hang tight…", { duration: 8000 });
      const r = await auditScrapeBacks(pw);
      if (!r?.ok) throw new Error(r?.error || "scrape failed");
      toast.success(
        `Scraped ${r.styles_attempted} styles · updated ${r.updated_products} products · ` +
        `+${r.colors_updated} back images · +${r.fronts_fixed} broken fronts refreshed. Re-running audit…`
      );
      run();
    } catch (e) {
      toast.error(e.message || "SanMar scrape failed");
    } finally {
      setScraping(false);
    }
  };

  const autofix = async () => {
    if (autofixing) return;
    setAutofixing(true);
    try {
      const dry = await auditAutoFix(pw, true);
      if (!dry?.ok) throw new Error(dry?.error || "dry-run failed");
      if (!dry.products_touched) {
        toast.info("Nothing to auto-fix — everything is clean.");
        setAutofixing(false);
        return;
      }
      const proceed = window.confirm(
        `Auto-fix will touch ${dry.products_touched} products:\n\n` +
        `  • Copy front → back for ${dry.copied_back_images} product${dry.copied_back_images === 1 ? "" : "s"} missing back images (SanMar doesn't shoot back views for hats/bags/tall/sleeveless).\n` +
        `  • Pad ${dry.padded_descriptions} description${dry.padded_descriptions === 1 ? "" : "s"} shorter than 60 chars.\n` +
        `  • Prune ${dry.removed_broken_urls} confirmed-dead image URL${dry.removed_broken_urls === 1 ? "" : "s"} (only truly-dead ones — transient 5xx are ignored).\n\n` +
        "Nothing is deleted — only additive/pruning changes. Continue?"
      );
      if (!proceed) { setAutofixing(false); return; }
      const applied = await auditAutoFix(pw, false);
      if (!applied?.ok && applied?.errors?.length) {
        toast.error(`Applied with warnings: ${applied.errors[0]?.slice(0, 160)}`);
      } else {
        toast.success(
          `Auto-fix applied — ${applied.products_touched} products touched · ` +
          `${applied.copied_back_images} back images copied · ` +
          `${applied.padded_descriptions} descriptions padded · ` +
          `${applied.removed_broken_urls} dead URLs pruned. Re-running audit…`
        );
      }
      run();  // re-audit so the card refreshes
    } catch (e) {
      toast.error(e.message || "Auto-fix failed");
    } finally {
      setAutofixing(false);
    }
  };

  const downloadCsv = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${backendUrl}/api/admin/health/audit/export.csv`, {
        headers: { "X-Admin-Password": pw },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `data-audit-${status?.run_id || "latest"}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const run = async () => {
    if (running) return;
    setRunning(true);
    try {
      const k = await adminHealthAuditRun(pw);
      if (!k?.ok) throw new Error(k?.detail || "failed to queue");
      toast.info("Data audit running…");
      const t = setInterval(async () => {
        const s = await adminHealthAuditStatus(pw);
        setStatus(s);
        if (s?.phase === "done" || s?.phase === "failed") {
          clearInterval(t); setPollTimer(null); setRunning(false);
          if (s?.phase === "failed") toast.error("Audit failed");
          else {
            const t2 = s.totals || {};
            // iter72 — `back_image_issues` = no_back + cloned + partial.
            // Old field `no_back_images` only counted the strict "no_back"
            // case which read as "0 missing" while ~16 products actually
            // had cloned/partial backs.
            const backIssues = t2.back_image_issues ?? t2.no_back_images ?? 0;
            toast[t2.with_issues > 0 ? "warning" : "success"](
              `Audit complete — ${t2.with_issues}/${t2.products} products have issues · ${t2.broken_urls} broken URLs · ${backIssues} with back-image gaps`
            );
          }
        }
      }, 3000);
      setPollTimer(t);
      setTimeout(() => { clearInterval(t); setPollTimer(null); setRunning(false); }, 180000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Audit failed");
      setRunning(false);
    }
  };

  const t = status?.totals || {};

  return (
    <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3" data-testid="health-audit-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
            <Database size={16} className="text-purple-400" /> Data Quality Audit
          </h3>
          <p className="font-mono text-[11px] text-zinc-500 mt-1">
            Scans <code className="text-zinc-300">db.products</code> for broken image URLs, missing back images, description/title length violations, duplicate SKUs/slugs.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button" onClick={scrapeFromSanmar}
            disabled={scraping || running || !status?.problems?.length}
            data-testid="health-audit-scrape-sanmar"
            title="Fetch sanmarcanada.com and pull REAL back photos + refresh broken front URLs for every product in the latest audit."
            className="border border-cyan-800 bg-cyan-950/40 text-cyan-200 hover:bg-cyan-900/50 px-3 py-2 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          >
            <RefreshCw size={13} className={scraping ? "animate-spin" : ""} />
            {scraping ? "SCRAPING…" : "SCRAPE SANMAR"}
          </button>
          <button
            type="button" onClick={autofix}
            disabled={autofixing || running || !status?.problems?.length}
            data-testid="health-audit-autofix"
            title="Automatically fix common issues: copy front→back where SanMar has no back photo, pad short descriptions, prune truly-dead URLs."
            className="border border-amber-800 bg-amber-950/40 text-amber-200 hover:bg-amber-900/50 px-3 py-2 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          >
            <Wand2 size={13} className={autofixing ? "animate-pulse" : ""} />
            {autofixing ? "FIXING…" : "AUTO-FIX ISSUES"}
          </button>
          <button
            type="button"
            onClick={async () => {
              const dry = await adminCategoriesNormalize(pw, true);
              if (dry.changed === 0) { toast.info("All categories already canonical."); return; }
              if (!window.confirm(
                `Normalize categories on ${dry.changed} products:\n` +
                Object.entries(dry.by_category || {}).map(([k,v]) => `  · ${k}: ${v}`).join("\n") +
                "\n\nSubcategories: " + Object.entries(dry.by_subcategory || {}).map(([k,v]) => `${k}:${v}`).join(", ") +
                "\n\nContinue?"
              )) return;
              const applied = await adminCategoriesNormalize(pw, false);
              toast.success(`Normalized ${applied.changed} products.`);
              run();  // re-audit
            }}
            data-testid="health-normalize-categories"
            title="Fold legacy category strings (t-shirts → tshirt) and infer subcategory (ladies / youth / adult) from product names using the classifier."
            className="border border-blue-800 bg-blue-950/40 text-blue-200 hover:bg-blue-900/50 px-3 py-2 font-mono text-xs tracking-widest flex items-center gap-2"
          >
            <Tag size={13} /> NORMALIZE CATEGORIES
          </button>
          <button
            type="button" onClick={downloadCsv}
            disabled={downloading || !status?.problems?.length}
            data-testid="health-audit-download-csv"
            title={status?.problems?.length ? "Download all problem products as CSV" : "Run an audit first"}
            className="border border-[#333] text-zinc-200 hover:border-zinc-500 px-3 py-2 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          >
            <Download size={13} className={downloading ? "animate-pulse" : ""} />
            {downloading ? "DOWNLOADING…" : "DOWNLOAD CSV"}
          </button>
          <button
            type="button" onClick={run} disabled={running}
            data-testid="health-audit-run"
            className="bg-purple-800 border border-purple-700 text-white px-4 py-2 font-mono text-xs tracking-widest hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw size={13} className={running ? "animate-spin" : ""} />
            {running ? "AUDITING…" : "RUN AUDIT NOW"}
          </button>
          <button
            type="button" onClick={scrapeSpecs} disabled={specRunning}
            data-testid="health-scrape-specs"
            title="Download SanMar PDF spec sheets and populate spec_sheet on every customize product. Runs in the background."
            className="bg-teal-800 border border-teal-700 text-white px-4 py-2 font-mono text-xs tracking-widest hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
          >
            <FileText size={13} className={specRunning ? "animate-pulse" : ""} />
            {specRunning ? "SCRAPING…" : "SCRAPE SPECS"}
          </button>
        </div>
      </div>

      {/* iter76 — live progress for the spec scraper triggered above */}
      {specStatus?.last_run?.phase === "running" && (
        <div className="border border-teal-900 bg-teal-950/20 p-2 flex items-center gap-2" data-testid="spec-scrape-progress">
          <FileText size={13} className="text-teal-400 animate-pulse shrink-0" />
          <span className="font-mono text-[11px] text-teal-200">
            SCRAPING SPECS · {specStatus.last_run.scraped || 0} processed · {specStatus.last_run.success || 0} OK · {specStatus.last_run.failed || 0} no PDF
          </span>
        </div>
      )}
      {specStatus?.last_run?.phase === "done" && specStatus?.last_run?.finished_at && (
        <div className="text-[10px] font-mono text-zinc-500">
          Last spec scrape · {specStatus.last_run.success}/{specStatus.last_run.scraped} succeeded · {specStatus.last_run.elapsed_s}s · <span className={specStatus.missing_count ? "text-amber-400" : "text-emerald-400"}>{specStatus.missing_count} products still missing spec</span>
        </div>
      )}

      {status?.phase === "done" ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <div className="border border-[#222] bg-black p-2"><div className="font-mono text-[9px] tracking-widest text-zinc-500">PRODUCTS</div><div className="font-display text-lg text-white">{t.products}</div></div>
            <div className="border border-[#222] bg-black p-2"><div className="font-mono text-[9px] tracking-widest text-zinc-500">WITH ISSUES</div><div className={`font-display text-lg ${t.with_issues ? "text-amber-300" : "text-emerald-300"}`}>{t.with_issues}</div></div>
            <div className="border border-[#222] bg-black p-2"><div className="font-mono text-[9px] tracking-widest text-zinc-500">BROKEN URLS</div><div className={`font-display text-lg ${t.broken_urls ? "text-red-300" : "text-emerald-300"}`}>{t.broken_urls}</div></div>
            <div className="border border-[#222] bg-black p-2"><div className="font-mono text-[9px] tracking-widest text-zinc-500">NO BACK IMG</div><div className={`font-display text-lg ${t.no_back_images ? "text-amber-300" : "text-emerald-300"}`}>{t.no_back_images}</div></div>
            <div className="border border-[#222] bg-black p-2"><div className="font-mono text-[9px] tracking-widest text-zinc-500">DUP SLUGS</div><div className={`font-display text-lg ${t.duplicate_slugs ? "text-red-300" : "text-emerald-300"}`}>{t.duplicate_slugs}</div></div>
            <div className="border border-[#222] bg-black p-2"><div className="font-mono text-[9px] tracking-widest text-zinc-500">DUP SKUS</div><div className={`font-display text-lg ${t.duplicate_skus ? "text-red-300" : "text-emerald-300"}`}>{t.duplicate_skus}</div></div>
          </div>

          {status.problems?.length > 0 && (
            <div className="border border-[#222] bg-black p-3">
              <button
                type="button"
                onClick={() => setExpanded((x) => !x)}
                data-testid="audit-toggle-problems"
                className="font-mono text-[11px] tracking-widest text-zinc-300 hover:text-white"
              >
                {expanded ? "▾" : "▸"} PROBLEM PRODUCTS ({status.problems.length}{status.problems.length >= 500 ? "+" : ""})
              </button>
              {expanded && (
                <div className="mt-3 max-h-[400px] overflow-y-auto space-y-1">
                  {status.problems.map((p) => (
                    <div key={p.id} className="border border-[#1a1a1a] bg-[#0a0a0a] p-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`audit-row-${p.id}`}>
                      <div className="font-mono text-[10px]">
                        <span className="text-zinc-500">{p.source || "manual"} · </span>
                        <span className="text-zinc-100">{p.name}</span>
                        <span className="text-zinc-500"> · {p.slug}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 items-center">
                        {p.codes.map((c, ix) => {
                          const key = `${p.id}::${c}`;
                          const busy = dismissingKey === key;
                          return (
                            <span
                              key={ix}
                              className={`border pl-1.5 pr-0.5 py-0.5 font-mono text-[9px] tracking-widest inline-flex items-center gap-1 ${
                                c.startsWith("broken_") ? "border-red-900 bg-red-950/30 text-red-300"
                                : c === "no_back_images" ? "border-amber-900 bg-amber-950/30 text-amber-300"
                                : "border-zinc-800 bg-zinc-950/30 text-zinc-400"
                              }`}
                              data-testid={`audit-code-${p.id}-${c.split(":")[0]}`}
                            >
                              {c}
                              <button
                                type="button"
                                onClick={() => dismissCode(p, c)}
                                disabled={busy}
                                title="Mark as OK — hide this flag from future audits (e.g. bag has no back photo)"
                                data-testid={`audit-dismiss-${p.id}-${c.split(":")[0]}`}
                                className="ml-0.5 px-1 hover:bg-black/40 text-zinc-400 hover:text-white disabled:opacity-40"
                              >
                                {busy ? "…" : <X size={9} />}
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Dismissed flags — collapsible so the operator can review + undo */}
          {dismissals.length > 0 && (
            <div className="border border-emerald-900 bg-emerald-950/10 p-3" data-testid="audit-dismissals-card">
              <button
                type="button"
                onClick={() => setDismissalsExpanded((x) => !x)}
                data-testid="audit-toggle-dismissals"
                className="font-mono text-[11px] tracking-widest text-emerald-200 hover:text-white"
              >
                {dismissalsExpanded ? "▾" : "▸"} DISMISSED FLAGS ({dismissals.length}) — MARKED OK
              </button>
              {dismissalsExpanded && (
                <div className="mt-3 max-h-[300px] overflow-y-auto space-y-1">
                  {dismissals.map((d) => {
                    const key = `${d.product_id}::${d.code_prefix}`;
                    const busy = dismissingKey === key;
                    return (
                      <div
                        key={key}
                        className="border border-[#1a2a1a] bg-black p-2 flex items-center justify-between gap-2 flex-wrap"
                        data-testid={`dismissal-row-${d.product_id}-${d.code_prefix}`}
                      >
                        <div className="font-mono text-[10px]">
                          <span className="text-zinc-100">{d.product_name || d.product_id}</span>
                          <span className="text-zinc-500"> · {d.product_slug || "—"}</span>
                          <span className="text-emerald-300"> · {d.code_prefix}</span>
                          {d.reason && (
                            <span className="text-zinc-500"> · &ldquo;{d.reason}&rdquo;</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => undismiss(d)}
                          disabled={busy}
                          data-testid={`dismissal-undo-${d.product_id}-${d.code_prefix}`}
                          className="border border-[#333] text-zinc-300 hover:border-red-700 hover:text-red-300 px-2 py-1 font-mono text-[9px] tracking-widest disabled:opacity-40 inline-flex items-center gap-1"
                        >
                          <RotateCcw size={9} /> {busy ? "…" : "UNDO"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {(Object.keys(status.duplicate_slugs || {}).length > 0 || Object.keys(status.duplicate_skus || {}).length > 0) && (
            <div className="border border-red-900 bg-red-950/10 p-3 space-y-1">
              {Object.entries(status.duplicate_slugs || {}).map(([slug, ids]) => (
                <div key={slug} className="font-mono text-[10px] text-red-200">
                  DUPLICATE SLUG <span className="text-white">{slug}</span> → {ids.join(", ")}
                </div>
              ))}
              {Object.entries(status.duplicate_skus || {}).map(([sku, ids]) => (
                <div key={sku} className="font-mono text-[10px] text-red-200">
                  DUPLICATE SKU <span className="text-white">{sku}</span> → {ids.join(", ")}
                </div>
              ))}
            </div>
          )}
          <div className="font-mono text-[9px] text-zinc-600">Last audit: {fmtTime(status.finished_at)} · run_id {status.run_id}</div>
        </>
      ) : status?.phase && status.phase !== "idle" ? (
        <div className="font-mono text-xs text-blue-300 flex items-center gap-2">
          <RefreshCw size={13} className="animate-spin" /> phase: {status.phase.toUpperCase()}
        </div>
      ) : (
        <div className="font-mono text-xs text-zinc-500">No audit has run yet — click RUN AUDIT NOW.</div>
      )}
    </div>
  );
}

// ============================================================
// D · CRON TIMELINE CARD
// ============================================================
function CronTimelineCard({ pw }) {
  const [tl, setTl] = useState({ feed_runs: [], audit_runs: [] });
  const load = useCallback(async () => {
    try { setTl(await adminHealthTimeline(pw, 20)); }
    catch (_e) { /* ignore */ }
  }, [pw]);
  useEffect(() => { load(); }, [load]);

  const strip = (runs, label, testid) => (
    <div>
      <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="flex gap-1 flex-wrap" data-testid={testid}>
        {runs.length === 0 && <span className="font-mono text-[10px] text-zinc-600">no runs yet</span>}
        {runs.slice().reverse().map((r) => (
          <span
            key={r.run_id}
            title={`${r.run_id}\n${fmtTime(r.started_at)}\nphase=${r.phase}\nok=${r.ok}`}
            className={`inline-block w-4 h-6 border ${
              r.phase !== "done" ? "border-blue-800 bg-blue-900/40"
              : r.ok ? "border-emerald-800 bg-emerald-700/70"
              : "border-red-800 bg-red-700/70"
            }`}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3" data-testid="health-timeline-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
            <Activity size={16} className="text-cyan-400" /> Cron Monitor
          </h3>
          <p className="font-mono text-[11px] text-zinc-500 mt-1">
            Feed-health cron runs every 6h at :53 past. Hover a cell for run details.
          </p>
        </div>
        <button type="button" onClick={load} data-testid="health-timeline-refresh"
          className="border border-[#333] text-zinc-300 hover:border-zinc-500 px-3 py-1.5 font-mono text-[11px] tracking-widest flex items-center gap-1">
          <RefreshCw size={11} /> REFRESH
        </button>
      </div>
      {strip(tl.feed_runs || [], "FEED HEALTH RUNS", "timeline-feed-runs")}
      {strip(tl.audit_runs || [], "DATA AUDIT RUNS", "timeline-audit-runs")}
    </div>
  );
}

// ============================================================
// MAIN TAB — composes the 4 cards
// ============================================================
export default function HealthTab({ pw }) {
  return (
    <div className="space-y-5" data-testid="health-tab">
      <div className="flex items-center gap-2">
        <h2 className="font-display text-2xl uppercase tracking-tight">Health &amp; Verification</h2>
        <span className="font-mono text-[10px] tracking-widest text-zinc-500">· PHASE 3</span>
      </div>
      <div className="grid xl:grid-cols-2 gap-5">
        <FeedHealthCard pw={pw} />
        <PixelHealthCard pw={pw} />
      </div>
      {/* iter76c — moved from Dashboard (per operator request) so all
          verification/health cards live under one roof. */}
      <div className="grid xl:grid-cols-2 gap-5">
        <WebhookHealthCard pw={pw} />
        <AltTextAuditCard pw={pw} />
      </div>
      <WebhookDeliveriesCard pw={pw} />
      <DataAuditCard pw={pw} />
      <CronTimelineCard pw={pw} />
    </div>
  );
}
